<template>
    <div ref="container" class="vs-chart-container" :style="reactiveContainerStyle">
        <svg
            v-if="draw"
            class="vs-chart-svg"
            :style="reactiveSvgStyle"
            @mousedown="handlerMouseDown"
            @mouseup="handlerMouseUp"
            @mousemove="handlermouseMove"
        >
            <SvgLineGradient v-if="gradients"
                :id="path.id"
                :gradient="path.gradient"
                :gradientDirection="gradientDirection"
            ></SvgLineGradient>
            <g class="area" v-if="area">
                <path v-for="(path, index) in pathArray" :key="index"
                    :opacity="opacity"
                    :d="path.areaPath"
                    :fill="`${gradients ? `url(#${path.id})` : `${path.color}`}`"
                ></path>
            </g>
            <g class="line">
                <path v-for="(path, index) in pathArray" :key="index"
                    :ref="`path-${path.key}`"
                    :d="path.linePath"
                    :stroke="`${gradients ? `url(#${path.id})` : `${path.color}`}`"
                    :stroke-width="strokeWidth"
                    fill="none"
                ></path>
            </g>
            <g>
                <rect
                    :x="rect.x"
                    :y="boundary.minY"
                    :width="rect.width"
                    :height="boundary.maxY > 0 ? boundary.maxY - boundary.padding : 0"
                    fill="rgba(217, 211, 178, 0.2)"
                ></rect>
            </g>
            <template v-for="(path, index) in pathArray">
                <g class="circle" v-if="circle && !smooth" :key="`p-${index}`">
                    <template v-for="(c, subIndex) in path.points">
                        <circle
                            v-if="c"
                            :key="subIndex"
                            style="cursor:pointer"
                            :cx="c && c.x"
                            :cy="c && c.y"
                            :r="hover && hoverIndex === subIndex ? 4 : 3"
                            :stroke="`${gradients ? `url(#${path.id})` : `${path.color}` }`"
                            :stroke-width="2"
                            :fill="`${hover && hoverIndex === subIndex ? path.color : 'white'}`"
                            @click="handlerCircleClick($event, c)"
                            @mouseover="handlerMouseOver($event, c, path.key, subIndex)"
                            @mouseout="handlerMouseOut"
                        >
                        </circle>
                    </template>
                </g>
            </template>
            <!-- evnet -->
            <g v-if="typeof eventIndex === 'number' && eventIndex > 0 && xPoints[eventIndex] && xPoints[eventIndex].x">
                <line
                    :x1="xPoints[eventIndex].x"
                    :x2="xPoints[eventIndex].x"
                    :y1="boundary.minY"
                    :y2="boundary.maxY"
                    :stroke="eventColor || 'rgba(190, 190, 190, 1)'"
                >
                </line>
            </g>
            <!-- common -->
            <g class="x-axis" :transform="`translate(-${boundary.minX}, 0)`">
                <AxisXLine
                    v-if="xAxis"
                    :boundary="boundary"
                    :strokeColor="strokeColor"
                >
                </AxisXLine>
                <AxisXLabel
                    v-if="xLabel"
                    :boundary="boundary"
                    :labelRotate="labelRotate"
                    :labelColor="labelColor"
                    :tickSize="tickSize"
                    :tickPadding="tickPadding"
                    :points="xPoints"
                    :strokeColor="strokeColor"
                >
                    <template v-slot:x-axis="{ item }">
                        <slot :item="{ ...item }" name="x-axis"></slot>
                    </template>
                </AxisXLabel>
            </g>
            <g class="y-axis" :transform="`translate(-${boundary.minX}, 0)`">
                <AxisYLine
                    v-if="yAxis"
                    :boundary="boundary"
                    :padding="padding"
                    :tickPadding="tickPadding"
                ></AxisYLine>
                <AxisYLabel
                    v-if="yLabel"
                    :boundary="boundary"
                    :labelColor="labelColor"
                    :tickSize="tickSize"
                    :tickPadding="tickPadding"
                    :points="yPoints"
                >
                    <template v-slot:y-axis="{ item }">
                        <slot :item="{ ...item }" name="y-axis"></slot>
                    </template>
                </AxisYLabel>
            </g>
            <Legend v-if="pathArray.length > 0"
                :yKeys="yKeys"
                :legendSize="legendSize"
                :legendPadding="legendPadding"
                :legendDirection="legendDirection"
                :boundary="boundary"
                :data="pathArray"
                :labelColor="labelColor"
                :colors="lineColor"
            >
                <template v-slot:legend="{ item }">
                    <slot :item="{ ...item }" name="legend"></slot>
                </template>
            </Legend>
            <g class="grid">
                <g class="y-grid" v-if="yGrid !== 0">
                    <line v-for="(point, index) in yPoints" :key="index"
                        :x1="boundary.minX"
                        :x2="boundary.maxX - boundary.padding"
                        :y1="boundary.padding + point.y"
                        :y2="boundary.padding + point.y"
                        :stroke="strokeColor"
                    ></line>
                </g>
                <g class="x-grid" v-if="xGrid !== 0">
                    <line v-for="(point, index) in xPoints.filter((point, index) => index !== 0 && index % xGrid === 0)" :key="index"
                        :x1="point.x"
                        :x2="point.x"
                        :y1="boundary.padding"
                        :y2="boundary.maxY"
                        :stroke="strokeColor"
                    >
                    </line>
                </g>
            </g>
            <slot :item="{ ...hoverData }" name="tooltip"></slot>
        </svg>
    </div>
</template>

<script>
import { genPoints, genPath } from '../helper/path'
import { genLineXaxisPoints, genYaxisPoints } from '../helper/axis'
import commonProps from '../mixins/commonProps'
import lineProps from '../mixins/lineProps'
import SvgLineGradient from './SvgLineGradient'
import Legend from './Legend'
import AxisXLine from './AxisXLine'
import AxisXLabel from './AxisXLabel'
import AxisYLine from './AxisYLine'
import AxisYLabel from './AxisYLabel'
export default {
    name: 'vs-svg-line',
    components: {
        SvgLineGradient,
        AxisXLine,
        AxisXLabel,
        AxisYLine,
        AxisYLabel,
        Legend
    },
    mixins: [
        commonProps,
        lineProps
    ],
    data () {
        return {
            draw: true,
            lastLength: 0,
            hover: false,
            hoverData: {},
            hoverIndex: -1,
            pointMap: {},
            pathArray: [],
            widthValue: 0,
            dragBox: false,
            mouseStatus: {
                down: false,
                move: false
            },
            rect: {
                x: 0,
                width: 0
            }
        }
    },
    computed: {
        xPoints () {
            return genLineXaxisPoints(this.data, this.xKey, this.boundary)
        },
        yPoints () {
            return genYaxisPoints(this.grid, this.boundary, { min: this.min, max: this.max })
        },
        reactiveContainerStyle () {
            return {
                'padding-top': this.height && this.width && this.draw ? `${this.height / this.width * (this.width / this.widthValue * 100)}%` : undefined
            }
        },
        reactiveSvgStyle () {
            const width = this.width / this.widthValue * 100
            return {
                height: `${this.height}px`,
                width: `${width}%`
            }
        },
        max () {
            if (this.maximumMax) {
                return this.maximumMax
            } else {
                const max = Math.max(...this.values, null)
                return max
            }
        },
        min () {
            return Math.min(...this.values, null)
        },
        grid () {
            const defaultGrid = this.data.length / 2 > 6 ? 6 : (this.data.length / 2 < 1 ? 4 : this.data.length)
            return {
                xGrid: this.xGrid || defaultGrid,
                yGrid: this.yGrid || defaultGrid
            }
        },
        // line
        values () {
            const { data, yKeys } = this
            const inArr = []
            data.forEach(item => {
                yKeys.map(key => {
                    if (typeof item[key] === 'number') {
                        inArr.push(item[key])
                    }
                })
            })
            return inArr
        },
        renderOptions () {
            const { smooth, circle, yKeys, lineColor, gradients } = this
            return {
                smooth,
                circle,
                yKeys,
                lineColor,
                gradients
            }
        },
        boundary () {
            const { padding, width, height } = this
            const maxValue = Math.max.apply(null, this.values)
            const maxLength = maxValue.toFixed(this.maxDecimal).length
            const isZero = this.values.some(value => value < 1)
            let margin = 0
            if (this.marginLeft === 'auto') {
                margin = maxLength * 5
                if (isZero && margin < 30) {
                    margin = 30
                }
            } else {
                margin = this.marginLeft
            }
            return {
                minX: padding + margin,
                minY: padding,
                maxX: width - padding,
                maxY: height - padding - padding,
                padding,
                marginLeft: margin
            }
        }
    },
    mounted () {
        this.$nextTick(() => {
            if (this.width && this.height) {
                this.makeDrawData()
            }
            window.addEventListener('resize', this.makeDrawData)
        })
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.makeDrawData)
    },
    methods: {
        resetRange () {
            this.dragBox = false
            this.rect.x = this.boundary.minX
            this.rect.width = 0
        },
        handlerCircleClick (event, circle) {
            if (this.$listeners['click:point']) {
                const index = this.xPoints.findIndex(point => point.x === circle.x)
                const data = this.data[index]
                this.$emit('click:point', { event, data })
            }
        },
        handlerMouseOver (event, circle, yKey, index) {
            const data = this.data[index]
            if (this.hoverIndex !== index && data[yKey] !== null && this.$listeners['mouseover:point'] && this.xPoints[index]) {
                this.hover = true
                this.hoverIndex = index
                const point = this.pointMap[yKey] && this.pointMap[yKey][index]
                this.hoverData = {
                    label: this.xPoints[index].label,
                    yKey,
                    index,
                    ...point,
                    data
                }
                this.$emit('mouseover:point', { event, data: this.hoverData })
            }
        },
        handlerMouseOut (event) {
            this.hover = false
            this.hoverIndex = -1
            this.$emit('mouseout:point', { event })
        },
        handlermouseMove (event) {
            if (this.$listeners['select:range'] && this.dragBox && this.mouseStatus.down) {
                const rectWidth = event.layerX - this.rect.x
                const maxWidth = this.boundary.maxX - this.boundary.padding - this.rect.x
                this.rect.width = rectWidth > 0 ? (rectWidth < maxWidth ? rectWidth : maxWidth) : 0
            }
        },
        handlerMouseUp (event) {
            if (this.mouseStatus.down && this.dragBox) {
                this.mouseStatus.down = false
                const startIndex = this.xPoints.findIndex(point => point.x >= this.rect.x)
                const endIndex = this.xPoints.findIndex(point => point.x >= this.rect.x + this.rect.width) - 1
                if (this.$listeners['select:range'] && startIndex >= 0 && endIndex >= 0 && (startIndex < endIndex || startIndex === endIndex)) {
                    this.$emit('select:range', { event, data: [this.xPoints[startIndex], this.xPoints[endIndex]] })
                }
            }
        },
        handlerMouseDown (event) {
            if (this.rect.width > 0 && this.dragBox) {
                this.dragBox = false
                this.rect.x = this.boundary.minX
                this.rect.width = 0
            } else {
                this.rect.x = event.layerX
                this.dragBox = true
                this.mouseStatus.down = true
            }
        },
        createPath (d, id, h) {
            return h('path', {
                attrs: { d, fill: 'none', stroke: `url(#${id})` }
            })
        },
        makeDrawData () {
            this.resetRange()
            if (this.yKeys.length === 0) {
                this.draw = false
                return
            }
            this.draw = true
            this.widthValue = this.$refs.container.getBoundingClientRect().width || 0
            this.pathArray = this.yKeys.map(key => {
                if (this.data.every(item => item[key] === null)) {
                    return {
                        id: null
                    }
                }
                const points = genPoints({ yGrid: this.yGrid }, this.boundary, { max: this.max, min: this.min }, this.data, key)
                this.$set(this.pointMap, key, points)
                const { linePath, areaPath } = genPath(points.slice(), this.smooth ? this.radius : 0, this.boundary.maxY)
                return {
                    id: `vs-chart-line-${this._uid}-${key}`,
                    gradient: this.gradients ? this.gradients[key] : undefined,
                    color: this.lineColor ? this.lineColor[key] : undefined,
                    linePath,
                    areaPath,
                    key,
                    points: points
                }
            }).filter(item => item.id).reverse()
        }
    },
    watch: {
        renderOptions: {
            handler (value) {
                this.makeDrawData()
            },
            deep: true
        },
        data: {
            handler (value) {
                this.makeDrawData()
            },
            deep: true
        },
        boundary: {
            handler (value) {
                this.makeDrawData()
            },
            deep: true
        },
        pathArray: {
            handler (value) {
                if (value.length === 0) return
                this.$nextTick(() => {
                    this.yKeys.forEach(key => {
                        if (!this.$refs[`path-${key}`] || !this.autoDraw) {
                            return
                        }
                        const paths = this.$refs[`path-${key}`]
                        if (paths && paths.length > 0) {
                            const path = paths[0]
                            const length = path.getTotalLength()
                            path.style.transition = 'none'
                            path.style.strokeDasharray = length + ' ' + length
                            path.style.strokeDashoffset = Math.abs(length - (this.lastLength || 0))
                            path.getBoundingClientRect()
                            path.style.transition = `stroke-dashoffset ${this.autoDrawDuration}ms ${this.autoDrawEasing}`
                            path.style.strokeDashoffset = 0
                            if (length > 0) {
                                this.lastLength = length
                            }
                        }
                    })
                })
            },
            immediate: true,
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
.vs-chart-container {
    height: 0;
    position: relative;
    .vs-chart-svg {
        position: absolute;
        top: 0;
        left: 0;
    }
}
</style>
